import { useCallback, useEffect, useState } from 'react';
import { Option, Select, Label, styled, ToastTypes } from '@streem/ui-react';
import { recordElementClicked } from '@streem/analytics';
import appLogger from '../../util/logging/app_logger';
import {
    getMediaDevices,
    getPreferredAudioOutputId,
    setPreferredAudioOutput,
} from '@streem/toolbox';
import { useGlobalStore } from '../../hooks/use_global_context';

const log = appLogger.extend('AudioOutputSelect');

type AudioOutputOption = Option<string>;
const convertMediaDeviceInfoToAudioOutputOption = (device: MediaDeviceInfo): AudioOutputOption => ({
    value: device.deviceId,
    label: device.label,
});

interface Props {
    open: boolean;
    onPermissionDenied: () => void;
    onPermissionAllowed: () => void;
}

const inputId = 'audio-output-select';
const audioOutputDropdownOpenedId = 'ms-audio-output-select-opened';
const audioOutputDropdownId = 'ms-audio-output-item-selected';

let mediaStream: MediaStream | null = null;

const AudioOutputSelect = ({ open, onPermissionDenied, onPermissionAllowed }: Props) => {
    const { uiStore } = useGlobalStore();
    const [audioOutputDevices, setAudioOutputDevices] = useState<AudioOutputOption[]>([]);
    const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] = useState<
        AudioOutputOption | undefined
    >(undefined);
    const [initialized, setInitialized] = useState(false);
    const [placeholder, setPlaceholder] = useState<string>('Speaker selection unavailable');

    const handleSelect = useCallback(
        (option: Option<string>) => {
            log.info('setting preferred audio output device', option);
            setPreferredAudioOutput(option.value, option.label);
            const previousSelectedAudioOutputDevice = selectedAudioOutputDevice;
            setSelectedAudioOutputDevice(option);
            recordElementClicked(audioOutputDropdownId);

            if (previousSelectedAudioOutputDevice) {
                uiStore.removeToast(previousSelectedAudioOutputDevice?.label);
            }

            uiStore.addToast({
                content: `Speaker changed to ${option.label}`,
                flavor: ToastTypes.INFO,
                id: option.label,
                timeout: 3000,
            });
        },
        [setSelectedAudioOutputDevice, selectedAudioOutputDevice, uiStore],
    );

    useEffect(() => {
        const getAudioOutputDevices = async () => {
            log.info('getting audio output devices');

            // ensure that the user has given permission to the website to use audio.  Without this,
            //     the list of audio devices will be empty.
            try {
                mediaStream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video: false,
                });
            } catch (e) {
                log.warn('call to getUserMedia failed for audio', e);
                onPermissionDenied();
                return;
            }

            onPermissionAllowed();
            // this should only be executed if the user has given permission to the website to use audio.  Also,
            // this doesn't work in Safari.
            try {
                const devices = await getMediaDevices('audiooutput');
                const options = devices.map(convertMediaDeviceInfoToAudioOutputOption);
                setAudioOutputDevices(options);
                if (options.length > 0) {
                    setPlaceholder('Select Speaker');
                }
            } catch (e) {
                log.error('error getting user audio devices', e);
            }
        };

        // only when the modal opens
        if (open) {
            getAudioOutputDevices();
        }

        return () => {
            if (mediaStream) {
                mediaStream.getAudioTracks().forEach(track => track.stop());
            }
        };
    }, [open, onPermissionDenied, onPermissionAllowed]);

    useEffect(() => {
        if (audioOutputDevices.length > 0) {
            const preferredAudioOutputId = getPreferredAudioOutputId();
            if (preferredAudioOutputId) {
                const preferredAudioOutput = audioOutputDevices.find(
                    device => device.value === preferredAudioOutputId,
                );
                if (preferredAudioOutput) {
                    // label may have changed if the default device changed between browser sessions
                    setPreferredAudioOutput(preferredAudioOutputId, preferredAudioOutput.label);
                    setSelectedAudioOutputDevice(preferredAudioOutput);
                } else {
                    const defaultOutputDevice = audioOutputDevices.find(
                        device => device.value === 'default',
                    );

                    if (defaultOutputDevice) {
                        log.info(
                            `Could not find preferred preferredAudioOutputId=${preferredAudioOutputId}
                        Setting to default instead: ${JSON.stringify(defaultOutputDevice)}`,
                        );
                        setPreferredAudioOutput('default', defaultOutputDevice.label);
                        setSelectedAudioOutputDevice(defaultOutputDevice);
                    } else {
                        log.error('Unexpected state: Default Audio Output device not found!');
                    }
                }
            }
            setInitialized(true);
        }
    }, [audioOutputDevices]);

    return (
        <Wrapper data-clarity-mask="true">
            <DropdownHeader size="medium" semibold data-clarity-unmask="true">
                Audio
            </DropdownHeader>
            <Label semibold size="medium" htmlFor={inputId} data-clarity-unmask="true">
                Select Speaker
            </Label>
            <Select
                onMenuOpen={() => recordElementClicked(audioOutputDropdownOpenedId)}
                options={audioOutputDevices}
                value={selectedAudioOutputDevice}
                menuPlacement="bottom"
                onSelect={handleSelect}
                isDisabled={!initialized}
                id="audio-output-select"
                border={true}
                placeholder={placeholder}
                styles={{
                    menuList: () => ({
                        // prevent the menu list from overflowing the bottom of the wrapper
                        overflowY: 'scroll',
                        maxHeight: '9rem',
                    }),
                    container: provided => ({
                        width: '290px',
                        ...provided,
                    }),
                    placeholder: base => ({
                        ...base,
                        textWrap: 'nowrap',
                    }),
                }}
                inputId={inputId}
            />
        </Wrapper>
    );
};

const Wrapper = styled.span({
    display: 'inline-block',
});

const DropdownHeader = styled(Label)({
    display: 'block',
    margin: '5px 0px',
    fontSize: '1.1rem',
});

export default AudioOutputSelect;
